import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { ResourceLoading } from "../../../../components/utils/ResourceLoading";
import { useDispatch, useSelector } from "react-redux";
import {
  deleteOfferCalculationAsync,
  getOfferCalculationAsync,
} from "../../../../slices/offer-calculation";
import { ArrowPathIcon, CalculatorIcon, TrashIcon } from "@heroicons/react/24/outline";
import { dispatchWithToast } from "../../../../components/Utils";
import ProConfirmation from "../../../../components/utils/ProConfirmation";
import { ResourceError } from "../../../../components/utils/ResourceError";
import { OfferCalculationInfo } from "../../../../components/dashboard/offers/calculations/calculation-info";
import "./calculation.css";

export const OfferCalculation = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const dispatch = useDispatch();
  const itemState = {
    ...useSelector((store) => {
      return store.offerCalculations;
    }),
  };
  const { isLoading, error, errors, calculation } = itemState;

  const [deletePromptOpen, setDeletePromptOpen] = useState(false);

  useEffect(() => {
    if (id && id !== "new") {
      dispatch(getOfferCalculationAsync(id));
    }
  }, [dispatch, id]);

  const handleDeletePrompt = () => {
    setDeletePromptOpen(true);
  };

  const navigate = useNavigate();

  const handleAfterDispatch = () => {
    setDeletePromptOpen(false);
    navigate("/dashboard/offers/calculations");
  };

  const handleDeleteItem = () => {
    dispatchWithToast(dispatch(deleteOfferCalculationAsync(calculation?.id)), null, () =>
      handleAfterDispatch(false)
    );
  };

  const totalAmount = Number(calculation?.module_result?.total || calculation?.total_amount || 0);
  const formattedTotal = new Intl.NumberFormat("nb-NO", {
    style: "currency",
    currency: "NOK",
    maximumFractionDigits: 0,
  }).format(Number.isFinite(totalAmount) ? totalAmount : 0);

  const renderContent = () => {
    if (isLoading && id !== "new") {
      return <ResourceLoading />;
    }
    if (error && errors) {
      return <ResourceError errors={errors} />;
    }

    return (
      <OfferCalculationInfo calculation={calculation} isLoading={isLoading} />
    );
  };

  return (
    <div className="offer-calculation-page">
      <header className="offer-calculation-page__header">
        <div className="offer-calculation-page__header-inner">
          <div>
            <div className="offer-calculation-page__kicker">
              {t("offer.calculation.page.kicker")}
            </div>
            <h1 className="offer-calculation-page__title">
              {calculation?.id
                ? calculation?.title || t("offer.calculation.untitled")
                : id === "new"
                ? t("offer.calculation.new")
                : t("offer.calculation.untitled")}
            </h1>
            <p className="offer-calculation-page__subtitle">
              {t("offer.calculation.page.subtitle")}
            </p>
            <div className="offer-calculation-page__meta">
              <span className="offer-calculation-page__pill is-accent">
                <CalculatorIcon className="h-4 w-4" />
                {formattedTotal}
              </span>
              <span className="offer-calculation-page__pill">
                {calculation?.active === false ? t("offer.calculation.inactive") : t("offer.calculation.active")}
              </span>
              <span className="offer-calculation-page__pill">
                {calculation?.module_enabled ? t("offer.calculation.builder.tab_modules") : t("offer.calculation.builder.tab_lines")}
              </span>
            </div>
          </div>
          <div className="offer-calculation-page__actions">
            {id !== "new" ? (
              <button
                className="offer-calculation-page__button"
                disabled={isLoading}
                onClick={() => dispatch(getOfferCalculationAsync(id))}
                type="button"
              >
                <ArrowPathIcon className="h-4 w-4" />
                {t("Refresh")}
              </button>
            ) : null}
            {id !== "new" ? (
              <button
                className="offer-calculation-page__button is-danger"
                disabled={isLoading}
                onClick={() => handleDeletePrompt(calculation)}
                type="button"
              >
                <TrashIcon className="h-4 w-4" />
                {t("offer.calculation.delete")}
              </button>
            ) : null}
          </div>
        </div>
      </header>

      <main className="offer-calculation-page__content">
        {renderContent()}
      </main>

      <ProConfirmation
        severity="confirmation"
        open={deletePromptOpen}
        message={"Denne kalkylen vil da bli fjernet for godt og kan ikke reverseres"}
        action="delete"
        module="offer_calculation"
        yesCallback={() => handleDeleteItem()}
        cancelCallback={() => setDeletePromptOpen(false)}
      />
    </div>
  );
};



